import { ACTION_TYPE, REPORT_NAME } from "../constant/reportType";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { alertMessage } from "../../utils/AlertService";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Loading from "../Loading";
import { getLocalStorageData } from "../../utils/LocalStorageService";

// ── Carbon Services ──────────────────────────────────────
import {
    useSaveCarbonSourceMasterMutation,
    useUpdateCarbonSourceMasterMutation,
    useGetCarbonSourceMasterByIdMutation,
    useBulkUploadCarbonSourceMasterMutation,
    useFindAllCarbonSourceMasterMutation,
} from "../../redux-toolkit/services/carbonSourceMasterService";
import {
    useSaveMonitoringBatchMutation,
    useUpdateMonitoringBatchMutation,
    useGetMonitoringBatchByIdMutation,
    useBulkUploadMonitoringBatchMutation,
    useFindAllMonitoringBatchMutation,
} from "../../redux-toolkit/services/monitoringBatchService";
import {
    useSaveVerificationRecordMutation,
    useUpdateVerificationRecordMutation,
    useGetVerificationRecordByIdMutation,
    useBulkUploadVerificationRecordMutation,
} from "../../redux-toolkit/services/verificationRecordService";

// ── Lookup Services ──────────────────────────────────────
import { useFindAllEntityMasterMutation } from "../../redux-toolkit/services/entityMasterService";
import { useFindAllEntityTypeMasterMutation } from "../../redux-toolkit/services/entityTypeMasterService";
import { useFindAllAssetTypeMutation } from "../../redux-toolkit/services/assetTypeService";
import { useFindAllAssetsMutation } from "../../redux-toolkit/services/assetsService";
import { useFindAllOpportunityTypeMutation } from "../../redux-toolkit/services/opportunityTypeService";
import { useFindAllOpportunityApplicationMutation } from "../../redux-toolkit/services/opportunityApplicationService";
import {
    useSaveProjectMutation,
    useUpdateProjectMutation,
    useGetProjectByIdMutation,
    useFindAllProjectsMutation
} from "../../redux-toolkit/services/projectIntegrationService";

type Props = {
    show: boolean;
    setShow: any;
    actionType: ACTION_TYPE;
    reportName: string;
    data: any;
    setCall: any;
};

const CarbonMasterData = (props: Props) => {
    const localUserObj: any = getLocalStorageData("localUser");

    const [getByIdData, setGetByIdData]: any = useState({});
    const [file, setFile] = useState<File | null>(null);
    const [activeTab, setActiveTab] = useState<"manual" | "bulk">("manual");

    // ── Lookup state ─────────────────────────────────────
    const [allEntities, setAllEntities] = useState<any[]>([]);
    const [allEntityTypes, setAllEntityTypes] = useState<any[]>([]);
    const [allAssetTypes, setAllAssetTypes] = useState<any[]>([]);
    const [allAssets, setAllAssets] = useState<any[]>([]);
    const [allOpportunityTypes, setAllOpportunityTypes] = useState<any[]>([]);
    const [allOpportunityApplications, setAllOpportunityApplications] = useState<any[]>([]);
    const [allCarbonSources, setAllCarbonSources] = useState<any[]>([]);
    const [allMonitoringBatchList, setAllMonitoringBatchList] = useState<any[]>([]);
    const [allProjects, setAllProjects] = useState<any[]>([]);

    // ── Carbon CRUD mutations ─────────────────────────────
    const [saveCarbonSource, saveCarbonSourceRes] = useSaveCarbonSourceMasterMutation();
    const [updateCarbonSource, updateCarbonSourceRes] = useUpdateCarbonSourceMasterMutation();
    const [getCarbonSourceById, carbonSourceByIdRes] = useGetCarbonSourceMasterByIdMutation();
    const [bulkUploadCarbonSource, bulkUploadCarbonSourceRes] = useBulkUploadCarbonSourceMasterMutation();

    const [saveMonitoringBatch, saveMonitoringBatchRes] = useSaveMonitoringBatchMutation();
    const [updateMonitoringBatch, updateMonitoringBatchRes] = useUpdateMonitoringBatchMutation();
    const [getMonitoringBatchById, monitoringBatchByIdRes] = useGetMonitoringBatchByIdMutation();
    const [bulkUploadMonitoringBatch, bulkUploadMonitoringBatchRes] = useBulkUploadMonitoringBatchMutation();

    const [saveVerificationRecord, saveVerificationRecordRes] = useSaveVerificationRecordMutation();
    const [updateVerificationRecord, updateVerificationRecordRes] = useUpdateVerificationRecordMutation();
    const [getVerificationRecordById, verificationRecordByIdRes] = useGetVerificationRecordByIdMutation();
    const [bulkUploadVerificationRecord, bulkUploadVerificationRecordRes] = useBulkUploadVerificationRecordMutation();

    const [saveProject, saveProjectRes] = useSaveProjectMutation();
    const [updateProject, updateProjectRes] = useUpdateProjectMutation();
    const [getProjectById, projectByIdRes] = useGetProjectByIdMutation();

    // ── Lookup mutations ──────────────────────────────────
    const [findAllEntity, findAllEntityRes] = useFindAllEntityMasterMutation();
    const [findAllEntityType, findAllEntityTypeRes] = useFindAllEntityTypeMasterMutation();
    const [findAllAssetType, findAllAssetTypeRes] = useFindAllAssetTypeMutation();
    const [findAllAssets, findAllAssetsRes] = useFindAllAssetsMutation();
    const [findAllOpportunityType, findAllOpportunityTypeRes] = useFindAllOpportunityTypeMutation();
    const [findAllOpportunityApplication, findAllOpportunityApplicationRes] = useFindAllOpportunityApplicationMutation();
    const [findAllCarbonSource, findAllCarbonSourceRes] = useFindAllCarbonSourceMasterMutation();
    const [findAllMonitoringBatch, findAllMonitoringBatchRes] = useFindAllMonitoringBatchMutation();
    const [findAllProjects, findAllProjectsRes] = useFindAllProjectsMutation();

    // ── Load lookups on mount ────────────────────────────
    useEffect(() => {
        const token = localUserObj?.token;
        findAllEntity({ token });
        findAllEntityType({ token });
        findAllAssetType({ token });
        findAllAssets({ token });
        findAllOpportunityType({ token });
        findAllOpportunityApplication({ token });
        findAllProjects({ token });

        // Extra lookups depending on report
        if (props.reportName === REPORT_NAME.MONITORING_BATCH) {
            findAllCarbonSource({ token });
        }
        if (props.reportName === REPORT_NAME.VERIFICATION_RECORD) {
            findAllMonitoringBatch({ token });
        }
    }, [props.reportName]);

    // ── Lookup response handlers ──────────────────────────
    useEffect(() => { if (findAllEntityRes.isSuccess) setAllEntities(findAllEntityRes.data?.data || findAllEntityRes.data || []); }, [findAllEntityRes.isSuccess]);
    useEffect(() => { if (findAllEntityTypeRes.isSuccess) setAllEntityTypes(findAllEntityTypeRes.data?.data || findAllEntityTypeRes.data || []); }, [findAllEntityTypeRes.isSuccess]);
    useEffect(() => { if (findAllAssetTypeRes.isSuccess) setAllAssetTypes(findAllAssetTypeRes.data?.data || findAllAssetTypeRes.data || []); }, [findAllAssetTypeRes.isSuccess]);
    useEffect(() => { if (findAllAssetsRes.isSuccess) setAllAssets(findAllAssetsRes.data?.data || findAllAssetsRes.data || []); }, [findAllAssetsRes.isSuccess]);
    useEffect(() => { if (findAllOpportunityTypeRes.isSuccess) setAllOpportunityTypes(findAllOpportunityTypeRes.data?.data || findAllOpportunityTypeRes.data || []); }, [findAllOpportunityTypeRes.isSuccess]);
    useEffect(() => { if (findAllOpportunityApplicationRes.isSuccess) setAllOpportunityApplications(findAllOpportunityApplicationRes.data?.data || findAllOpportunityApplicationRes.data || []); }, [findAllOpportunityApplicationRes.isSuccess]);
    useEffect(() => { if (findAllCarbonSourceRes.isSuccess) setAllCarbonSources(findAllCarbonSourceRes.data?.data || findAllCarbonSourceRes.data || []); }, [findAllCarbonSourceRes.isSuccess]);
    useEffect(() => { if (findAllMonitoringBatchRes.isSuccess) setAllMonitoringBatchList(findAllMonitoringBatchRes.data?.data || findAllMonitoringBatchRes.data || []); }, [findAllMonitoringBatchRes.isSuccess]);
    useEffect(() => { if (findAllProjectsRes.isSuccess) setAllProjects(findAllProjectsRes.data?.data || findAllProjectsRes.data || []); }, [findAllProjectsRes.isSuccess]);

    // ── GET BY ID on edit ────────────────────────────────
    useEffect(() => {
        if (props.actionType === ACTION_TYPE.UPDATE_ACTION && props.data?.id) {
            if (props.reportName === REPORT_NAME.CARBON_SOURCE_MASTER) {
                getCarbonSourceById({ token: localUserObj?.token, id: props.data.id });
            } else if (props.reportName === REPORT_NAME.MONITORING_BATCH) {
                getMonitoringBatchById({ token: localUserObj?.token, id: props.data.id });
            } else if (props.reportName === REPORT_NAME.VERIFICATION_RECORD) {
                getVerificationRecordById({ token: localUserObj?.token, id: props.data.id });
            } else if (props.reportName === REPORT_NAME.CARBON_PROJECT_MASTER) {
                getProjectById({ token: localUserObj?.token, id: props.data.id });
            }
        }
    }, [props.reportName, props.data?.id]);

    useEffect(() => { if (carbonSourceByIdRes.isSuccess) setGetByIdData(carbonSourceByIdRes.data || {}); }, [carbonSourceByIdRes.isSuccess]);
    useEffect(() => { if (monitoringBatchByIdRes.isSuccess) setGetByIdData(monitoringBatchByIdRes.data || {}); }, [monitoringBatchByIdRes.isSuccess]);
    useEffect(() => { if (verificationRecordByIdRes.isSuccess) setGetByIdData(verificationRecordByIdRes.data || {}); }, [verificationRecordByIdRes.isSuccess]);
    useEffect(() => { if (projectByIdRes.isSuccess) setGetByIdData(projectByIdRes.data || {}); }, [projectByIdRes.isSuccess]);

    // ── Save effects ─────────────────────────────────────
    useEffect(() => {
        if (saveCarbonSourceRes.isSuccess) { alertMessage("Carbon Source Master saved", "success"); props.setCall("Y"); handleClose(); }
        else if (saveCarbonSourceRes.isError) alertMessage("Error saving Carbon Source Master", "error");
    }, [saveCarbonSourceRes.isSuccess, saveCarbonSourceRes.isError]);

    useEffect(() => {
        if (saveMonitoringBatchRes.isSuccess) { alertMessage("Monitoring Batch saved", "success"); props.setCall("Y"); handleClose(); }
        else if (saveMonitoringBatchRes.isError) alertMessage("Error saving Monitoring Batch", "error");
    }, [saveMonitoringBatchRes.isSuccess, saveMonitoringBatchRes.isError]);

    useEffect(() => {
        if (saveVerificationRecordRes.isSuccess) { alertMessage("Verification Record saved", "success"); props.setCall("Y"); handleClose(); }
        else if (saveVerificationRecordRes.isError) alertMessage("Error saving Verification Record", "error");
    }, [saveVerificationRecordRes.isSuccess, saveVerificationRecordRes.isError]);

    useEffect(() => {
        if (saveProjectRes.isSuccess) { alertMessage("Project saved", "success"); props.setCall("Y"); handleClose(); }
        else if (saveProjectRes.isError) alertMessage("Error saving Project", "error");
    }, [saveProjectRes.isSuccess, saveProjectRes.isError]);

    // ── Update effects ────────────────────────────────────
    useEffect(() => {
        if (updateCarbonSourceRes.isSuccess) { alertMessage("Carbon Source Master updated", "success"); props.setCall("Y"); handleClose(); }
        else if (updateCarbonSourceRes.isError) alertMessage("Error updating Carbon Source Master", "error");
    }, [updateCarbonSourceRes.isSuccess, updateCarbonSourceRes.isError]);

    useEffect(() => {
        if (updateMonitoringBatchRes.isSuccess) { alertMessage("Monitoring Batch updated", "success"); props.setCall("Y"); handleClose(); }
        else if (updateMonitoringBatchRes.isError) alertMessage("Error updating Monitoring Batch", "error");
    }, [updateMonitoringBatchRes.isSuccess, updateMonitoringBatchRes.isError]);

    useEffect(() => {
        if (updateVerificationRecordRes.isSuccess) { alertMessage("Verification Record updated", "success"); props.setCall("Y"); handleClose(); }
        else if (updateVerificationRecordRes.isError) alertMessage("Error updating Verification Record", "error");
    }, [updateVerificationRecordRes.isSuccess, updateVerificationRecordRes.isError]);

    useEffect(() => {
        if (updateProjectRes.isSuccess) { alertMessage("Project updated", "success"); props.setCall("Y"); handleClose(); }
        else if (updateProjectRes.isError) alertMessage("Error updating Project", "error");
    }, [updateProjectRes.isSuccess, updateProjectRes.isError]);

    // ── Bulk upload effects ───────────────────────────────
    useEffect(() => {
        if (bulkUploadCarbonSourceRes.isSuccess) { alertMessage("Bulk upload successful", "success"); props.setCall("Y"); handleClose(); }
        else if (bulkUploadCarbonSourceRes.isError) alertMessage("Error during bulk upload", "error");
    }, [bulkUploadCarbonSourceRes.isSuccess, bulkUploadCarbonSourceRes.isError]);

    useEffect(() => {
        if (bulkUploadMonitoringBatchRes.isSuccess) { alertMessage("Bulk upload successful", "success"); props.setCall("Y"); handleClose(); }
        else if (bulkUploadMonitoringBatchRes.isError) alertMessage("Error during bulk upload", "error");
    }, [bulkUploadMonitoringBatchRes.isSuccess, bulkUploadMonitoringBatchRes.isError]);

    useEffect(() => {
        if (bulkUploadVerificationRecordRes.isSuccess) { alertMessage("Bulk upload successful", "success"); props.setCall("Y"); handleClose(); }
        else if (bulkUploadVerificationRecordRes.isError) alertMessage("Error during bulk upload", "error");
    }, [bulkUploadVerificationRecordRes.isSuccess, bulkUploadVerificationRecordRes.isError]);

    const handleClose = () => props.setShow(false);

    // ── Bulk upload handler ───────────────────────────────
    const handleBulkUpload = () => {
        if (!file) { alertMessage("Please select a file", "error"); return; }
        const formData = new FormData();
        formData.append("file", file);
        if (props.reportName === REPORT_NAME.CARBON_SOURCE_MASTER) bulkUploadCarbonSource({ token: localUserObj?.token, payload: formData });
        else if (props.reportName === REPORT_NAME.MONITORING_BATCH) bulkUploadMonitoringBatch({ token: localUserObj?.token, payload: formData });
        else if (props.reportName === REPORT_NAME.VERIFICATION_RECORD) bulkUploadVerificationRecord({ token: localUserObj?.token, payload: formData });
    };

    // ── Submit ────────────────────────────────────────────
    const handleSubmit = (values: any) => {
        const isAdd = props.actionType === ACTION_TYPE.ADD_ACTION;

        if (props.reportName === REPORT_NAME.CARBON_SOURCE_MASTER) {
            const payload = {
                ...(isAdd ? {} : { id: props.data?.id }),
                projectId: values.projectId ? Number(values.projectId) : null,
                latitude: values.latitude ? parseFloat(values.latitude) : null,
                longitude: values.longitude ? parseFloat(values.longitude) : null,
                areaHa: values.areaHa ? parseFloat(values.areaHa) : null,
                baselineCarbon: values.baselineCarbon || null,
                entityId: values.entityId ? Number(values.entityId) : null,
                entityTypeId: values.entityTypeId ? Number(values.entityTypeId) : null,
                parentEntityId: values.parentEntityId ? Number(values.parentEntityId) : null,
                parentEntityTypeId: values.parentEntityTypeId ? Number(values.parentEntityTypeId) : null,
                assetId: values.assetId ? Number(values.assetId) : null,
                assetTypeId: values.assetTypeId ? Number(values.assetTypeId) : null,
                parentAssetId: values.parentAssetId ? Number(values.parentAssetId) : null,
                parentAssetTypeId: values.parentAssetTypeId ? Number(values.parentAssetTypeId) : null,
                opportunityApplicationId: values.opportunityApplicationId ? Number(values.opportunityApplicationId) : null,
                opportunityTypeId: values.opportunityTypeId ? Number(values.opportunityTypeId) : null,
                planStart: values.planStart || null,
                planEnd: values.planEnd || null,
                actualStart: values.actualStart || null,
                actualEnd: values.actualEnd || null,
                status: isAdd ? true : props.data?.status,
            };
            if (isAdd) saveCarbonSource({ token: localUserObj?.token, payload });
            else updateCarbonSource({ token: localUserObj?.token, payload });

        } else if (props.reportName === REPORT_NAME.MONITORING_BATCH) {
            const payload = {
                ...(isAdd ? {} : { id: props.data?.id }),
                sourceId: values.sourceId ? Number(values.sourceId) : null,
                periodStart: values.periodStart || null,
                periodEnd: values.periodEnd || null,
                measuredCarbon: values.measuredCarbon ? parseFloat(values.measuredCarbon) : null,
                sequestration: values.sequestration ? parseFloat(values.sequestration) : null,
                entityId: values.entityId ? Number(values.entityId) : null,
                entityTypeId: values.entityTypeId ? Number(values.entityTypeId) : null,
                parentEntityId: values.parentEntityId ? Number(values.parentEntityId) : null,
                parentEntityTypeId: values.parentEntityTypeId ? Number(values.parentEntityTypeId) : null,
                assetId: values.assetId ? Number(values.assetId) : null,
                assetTypeId: values.assetTypeId ? Number(values.assetTypeId) : null,
                parentAssetId: values.parentAssetId ? Number(values.parentAssetId) : null,
                parentAssetTypeId: values.parentAssetTypeId ? Number(values.parentAssetTypeId) : null,
                opportunityApplicationId: values.opportunityApplicationId ? Number(values.opportunityApplicationId) : null,
                opportunityTypeId: values.opportunityTypeId ? Number(values.opportunityTypeId) : null,
                planStart: values.planStart || null,
                planEnd: values.planEnd || null,
                actualStart: values.actualStart || null,
                actualEnd: values.actualEnd || null,
                uom: values.uom || null,
                remarks: values.remarks || null,
                status: isAdd ? true : props.data?.status,
            };
            if (isAdd) saveMonitoringBatch({ token: localUserObj?.token, payload });
            else updateMonitoringBatch({ token: localUserObj?.token, payload });

        } else if (props.reportName === REPORT_NAME.VERIFICATION_RECORD) {
            const payload = {
                ...(isAdd ? {} : { id: props.data?.id }),
                batchId: values.batchId ? Number(values.batchId) : null,
                verifierName: values.verifierName || null,
                approvedQty: values.approvedQty ? parseInt(values.approvedQty) : null,
                bufferPercentage: values.bufferPercentage ? parseFloat(values.bufferPercentage) : null,
                entityId: values.entityId ? Number(values.entityId) : null,
                entityTypeId: values.entityTypeId ? Number(values.entityTypeId) : null,
                parentEntityId: values.parentEntityId ? Number(values.parentEntityId) : null,
                parentEntityTypeId: values.parentEntityTypeId ? Number(values.parentEntityTypeId) : null,
                assetId: values.assetId ? Number(values.assetId) : null,
                assetTypeId: values.assetTypeId ? Number(values.assetTypeId) : null,
                parentAssetId: values.parentAssetId ? Number(values.parentAssetId) : null,
                parentAssetTypeId: values.parentAssetTypeId ? Number(values.parentAssetTypeId) : null,
                opportunityApplicationId: values.opportunityApplicationId ? Number(values.opportunityApplicationId) : null,
                opportunityTypeId: values.opportunityTypeId ? Number(values.opportunityTypeId) : null,
                planStart: values.planStart || null,
                planEnd: values.planEnd || null,
                actualStart: values.actualStart || null,
                actualEnd: values.actualEnd || null,
                remarks: values.remarks || null,
                status: isAdd ? true : props.data?.status,
            };
            if (isAdd) saveVerificationRecord({ token: localUserObj?.token, payload });
            else updateVerificationRecord({ token: localUserObj?.token, payload });
        } else if (props.reportName === REPORT_NAME.CARBON_PROJECT_MASTER) {
            const payload = {
                ...(isAdd ? {} : { id: props.data?.id }),
                name: values.name || null,
                description: values.description || null,
                methodologyCode: values.methodologyCode || null,
                projectTypeId: values.projectTypeId ? Number(values.projectTypeId) : null,
                sponsor: values.sponsor || null,
                participatingOrganization: values.participatingOrganization || null,
                entityId: values.entityId ? Number(values.entityId) : null,
                entityTypeId: values.entityTypeId ? Number(values.entityTypeId) : null,
                parentEntityId: values.parentEntityId ? Number(values.parentEntityId) : null,
                parentEntityTypeId: values.parentEntityTypeId ? Number(values.parentEntityTypeId) : null,
                assetId: values.assetId ? Number(values.assetId) : null,
                assetTypeId: values.assetTypeId ? Number(values.assetTypeId) : null,
                parentAssetId: values.parentAssetId ? Number(values.parentAssetId) : null,
                parentAssetTypeId: values.parentAssetTypeId ? Number(values.parentAssetTypeId) : null,
                opportunityApplicationId: values.opportunityApplicationId ? Number(values.opportunityApplicationId) : null,
                opportunityTypeId: values.opportunityTypeId ? Number(values.opportunityTypeId) : null,
                planStart: values.planStart || null,
                planEnd: values.planEnd || null,
                actualStart: values.actualStart || null,
                actualEnd: values.actualEnd || null,
                status: isAdd ? true : props.data?.status,
            };
            if (isAdd) saveProject({ token: localUserObj?.token, payload });
            else updateProject({ token: localUserObj?.token, payload });
        }
    };

    // ── Initial values ────────────────────────────────────
    const getInitialValues = () => {
        const isEdit = props.actionType === ACTION_TYPE.UPDATE_ACTION;
        const d = isEdit ? getByIdData : {};
        const ts = (v: any) => v ? String(v).slice(0, 10) : "";

        // Common relation fields
        const common = {
            entityId: d?.entityId || d?.entity?.id || d?.entityMaster?.id || d?.monitoringBatch?.entity?.id || "",
            entityTypeId: d?.entityTypeId || d?.entityType?.id || d?.entityTypeMaster?.id || d?.monitoringBatch?.entityType?.id || "",
            parentEntityId: d?.parentEntityId || d?.parentEntity?.id || "",
            parentEntityTypeId: d?.parentEntityTypeId || d?.parentEntityType?.id || "",
            assetId: d?.assetId || d?.asset?.id || "",
            assetTypeId: d?.assetTypeId || d?.assetType?.id || "",
            parentAssetId: d?.parentAssetId || d?.parentAsset?.id || "",
            parentAssetTypeId: d?.parentAssetTypeId || d?.parentAssetType?.id || "",
            opportunityApplicationId: d?.opportunityApplicationId || d?.opportunityApplication?.id || "",
            opportunityTypeId: d?.opportunityTypeId || d?.opportunityType?.id || "",
            planStart: ts(d?.planStart),
            planEnd: ts(d?.planEnd),
            actualStart: ts(d?.actualStart),
            actualEnd: ts(d?.actualEnd),
        };

        if (props.reportName === REPORT_NAME.CARBON_SOURCE_MASTER) {
            return { projectId: d?.projectId || "", latitude: d?.latitude || "", longitude: d?.longitude || "", areaHa: d?.areaHa || "", baselineCarbon: d?.baselineCarbon || "", ...common };
        } else if (props.reportName === REPORT_NAME.MONITORING_BATCH) {
            return { sourceId: d?.sourceId || d?.sourceMaster?.id || "", periodStart: ts(d?.periodStart), periodEnd: ts(d?.periodEnd), measuredCarbon: d?.measuredCarbon || "", sequestration: d?.sequestration || "", uom: d?.uom || "", remarks: d?.remarks || "", ...common };
        } else if (props.reportName === REPORT_NAME.VERIFICATION_RECORD) {
            return { batchId: d?.batchId || d?.monitoringBatch?.id || "", verifierName: d?.verifierName || d?.verifier?.name || "", approvedQty: d?.approvedQty || "", bufferPercentage: d?.bufferPercentage || "", remarks: d?.remarks || "", ...common };
        } else if (props.reportName === REPORT_NAME.CARBON_PROJECT_MASTER) {
            return {
                name: d?.name || "",
                description: d?.description || "",
                methodologyCode: d?.methodologyCode || "",
                projectTypeId: d?.projectTypeId || "",
                sponsor: d?.sponsor || "",
                participatingOrganization: d?.participatingOrganization || "",
                ...common
            };
        }
        return {};
    };

    // ── Shared dropdown select ────────────────────────────
    const handleDropdownChange = (e: any, setFieldValue: any) => {
        const { name, value } = e.target;
        setFieldValue(name, value);

        // Auto-selection logic
        if (name === "entityId" && value) {
            const selected = allEntities.find(x => x.id == value);
            if (selected?.entityType?.id) setFieldValue("entityTypeId", String(selected.entityType.id));
        } else if (name === "parentEntityId" && value) {
            const selected = allEntities.find(x => x.id == value);
            if (selected?.entityType?.id) setFieldValue("parentEntityTypeId", String(selected.entityType.id));
        } else if (name === "assetId" && value) {
            const selected = allAssets.find(x => x.id == value);
            if (selected?.assetType?.id) setFieldValue("assetTypeId", String(selected.assetType.id));
        } else if (name === "parentAssetId" && value) {
            const selected = allAssets.find(x => x.id == value);
            if (selected?.assetType?.id) setFieldValue("parentAssetTypeId", String(selected.assetType.id));
        } else if (name === "opportunityApplicationId" && value) {
            const selected = allOpportunityApplications.find(x => x.id == value);
            if (selected?.opportunityType?.id) setFieldValue("opportunityTypeId", String(selected.opportunityType.id));
        }
    };

    const SelectField = ({ name, label, options, labelKey = "name", valueKey = "id", setFieldValue, value }: any) => {
        const getLabel = (opt: any) => {
            const val = opt[labelKey] || opt.name || opt.assetRef || opt.id;
            if (typeof val === "object" && val !== null) {
                return val.name || val.code || val.projectName || val.id || JSON.stringify(val);
            }
            return val;
        };

        return (
            <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">{label}</label>
                <select
                    className="form-select"
                    name={name}
                    value={value}
                    onChange={(e) => handleDropdownChange(e, setFieldValue)}
                >
                    <option value="">-- Select {label} --</option>
                    {(options || []).map((opt: any) => (
                        <option key={opt[valueKey]} value={opt[valueKey]}>
                            {String(getLabel(opt))}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    // ── Common relation fields JSX ────────────────────────
    const renderCommonFields = (values: any, setFieldValue: any) => {
        const handleChange = (e: any) => setFieldValue(e.target.name, e.target.value);
        return (
            <>
                <SelectField name="entityId" label="Entity" options={allEntities} setFieldValue={setFieldValue} value={values.entityId} />
                <SelectField name="entityTypeId" label="Entity Type" options={allEntityTypes} setFieldValue={setFieldValue} value={values.entityTypeId} />
                <SelectField name="parentEntityId" label="Parent Entity" options={allEntities} setFieldValue={setFieldValue} value={values.parentEntityId} />
                <SelectField name="parentEntityTypeId" label="Parent Entity Type" options={allEntityTypes} setFieldValue={setFieldValue} value={values.parentEntityTypeId} />
                <SelectField name="assetId" label="Asset" options={allAssets} setFieldValue={setFieldValue} value={values.assetId} />
                <SelectField name="assetTypeId" label="Asset Type" options={allAssetTypes} setFieldValue={setFieldValue} value={values.assetTypeId} />
                <SelectField name="parentAssetId" label="Parent Asset" options={allAssets} setFieldValue={setFieldValue} value={values.parentAssetId} />
                <SelectField name="parentAssetTypeId" label="Parent Asset Type" options={allAssetTypes} setFieldValue={setFieldValue} value={values.parentAssetTypeId} />
                <SelectField name="opportunityTypeId" label="Opportunity Type" options={allOpportunityTypes} setFieldValue={setFieldValue} value={values.opportunityTypeId} />
                <SelectField name="opportunityApplicationId" label="Opportunity Application" options={allOpportunityApplications} labelKey="opportunityId" setFieldValue={setFieldValue} value={values.opportunityApplicationId} />
                <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Plan Start</label>
                    <input type="date" className="form-control" name="planStart" value={values.planStart} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Plan End</label>
                    <input type="date" className="form-control" name="planEnd" value={values.planEnd} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Actual Start</label>
                    <input type="date" className="form-control" name="actualStart" value={values.actualStart} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Actual End</label>
                    <input type="date" className="form-control" name="actualEnd" value={values.actualEnd} onChange={handleChange} />
                </div>
            </>
        );
    };

    // ── Per-report specific fields ────────────────────────
    const renderSpecificFields = (values: any, setFieldValue: any) => {
        const handleChange = (e: any) => setFieldValue(e.target.name, e.target.value);

        if (props.reportName === REPORT_NAME.CARBON_SOURCE_MASTER) {
            return (
                <>
                    <SelectField name="projectId" label="Project" options={allProjects} labelKey="projectName" setFieldValue={setFieldValue} value={values.projectId} />
                    {/* Geolocation */}
                    <div className="col-md-12 mb-3">
                        <label className="form-label fw-semibold">Location</label>
                        <div className="d-flex gap-2 align-items-start">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                    if (!navigator.geolocation) { alert("Geolocation not supported."); return; }
                                    navigator.geolocation.getCurrentPosition(
                                        (pos) => {
                                            setFieldValue("latitude", String(pos.coords.latitude));
                                            setFieldValue("longitude", String(pos.coords.longitude));
                                        },
                                        () => alert("Unable to fetch location. Please allow location access.")
                                    );
                                }}
                            >
                                📍 Allow Location
                            </button>
                            <input type="text" className="form-control form-control-sm" name="latitude" value={values.latitude} readOnly placeholder="Latitude" />
                            <input type="text" className="form-control form-control-sm" name="longitude" value={values.longitude} readOnly placeholder="Longitude" />
                        </div>
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Area (Ha)</label>
                        <input type="number" step="any" className="form-control" name="areaHa" value={values.areaHa} onChange={handleChange} placeholder="Enter Area in Ha" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Baseline Carbon</label>
                        <input type="text" className="form-control" name="baselineCarbon" value={values.baselineCarbon} onChange={handleChange} placeholder="Enter Baseline Carbon" />
                    </div>
                </>
            );
        } else if (props.reportName === REPORT_NAME.MONITORING_BATCH) {
            return (
                <>
                    {/* Carbon Source picks up code from sourceMaster.code when labelKey is fallback */}
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Carbon Source</label>
                        <select className="form-select" name="sourceId" value={values.sourceId} onChange={(e) => handleDropdownChange(e, setFieldValue)}>
                            <option value="">-- Select Carbon Source --</option>
                            {allCarbonSources.map((src: any) => (
                                <option key={src.id} value={src.id}>
                                    {src.assetRef?.name || src.code || `Source #${src.id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Measured Carbon</label>
                        <input type="number" step="any" className="form-control" name="measuredCarbon" value={values.measuredCarbon} onChange={handleChange} placeholder="Enter Measured Carbon" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Unit of Measurement</label>
                        <select className="form-select" name="uom" value={values.uom} onChange={handleChange}>
                            <option value="">-- Select UOM --</option>
                            <option value="Kilograms (kg CO₂e)">Kilograms (kg CO₂e)</option>
                            <option value="Tonnes (t CO₂e)">Tonnes (t CO₂e)</option>
                        </select>
                    </div>
                    <div className="col-md-12 mb-3">
                        <label className="form-label fw-semibold">Sequestration</label>
                        <textarea className="form-control" name="sequestration" value={values.sequestration} onChange={handleChange} placeholder="Enter Sequestration" rows={3} />
                    </div>
                    <div className="col-md-12 mb-3">
                        <label className="form-label fw-semibold">Remarks</label>
                        <textarea className="form-control" name="remarks" value={values.remarks} onChange={handleChange} placeholder="Enter Remarks" rows={3} />
                    </div>
                </>
            );
        } else if (props.reportName === REPORT_NAME.VERIFICATION_RECORD) {
            return (
                <>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Monitoring Batch</label>
                        <select className="form-select" name="batchId" value={values.batchId} onChange={(e) => handleDropdownChange(e, setFieldValue)}>
                            <option value="">-- Select Monitoring Batch --</option>
                            {allMonitoringBatchList.map((b: any) => (
                                <option key={b.id} value={b.id}>
                                    #{b.id} — {b.sourceMaster?.assetRef?.name || b.sourceMaster?.code || "Batch"} ({b.periodStart ? String(b.periodStart).slice(0, 10) : "?"} → {b.periodEnd ? String(b.periodEnd).slice(0, 10) : "?"})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Verifier Name</label>
                        <input type="text" className="form-control" name="verifierName" value={values.verifierName} onChange={handleChange} placeholder="Enter Verifier Name" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Approved Qty</label>
                        <input type="number" className="form-control" name="approvedQty" value={values.approvedQty} onChange={handleChange} placeholder="Enter Approved Qty" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Buffer Percentage (%)</label>
                        <input type="number" step="any" className="form-control" name="bufferPercentage" value={values.bufferPercentage} onChange={handleChange} placeholder="Enter Buffer %" />
                    </div>
                    <div className="col-md-12 mb-3">
                        <label className="form-label fw-semibold">Remarks</label>
                        <textarea className="form-control" name="remarks" value={values.remarks} onChange={handleChange} placeholder="Enter Remarks" rows={3} />
                    </div>
                </>
            );
        } else if (props.reportName === REPORT_NAME.CARBON_PROJECT_MASTER) {
            return (
                <>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Project Name</label>
                        <input type="text" className="form-control" name="name" value={values.name} onChange={handleChange} placeholder="Enter Project Name" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Methodology Code</label>
                        <input type="text" className="form-control" name="methodologyCode" value={values.methodologyCode} onChange={handleChange} placeholder="e.g. AR-ACM0003" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Sponsor</label>
                        <input type="text" className="form-control" name="sponsor" value={values.sponsor} onChange={handleChange} placeholder="Enter Project Sponsor" />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold">Participating Org</label>
                        <input type="text" className="form-control" name="participatingOrganization" value={values.participatingOrganization} onChange={handleChange} placeholder="Enter Org Name" />
                    </div>
                    <div className="col-md-12 mb-3">
                        <label className="form-label fw-semibold">Description</label>
                        <textarea className="form-control" name="description" value={values.description} onChange={handleChange} placeholder="Project Description" rows={3} />
                    </div>
                </>
            );
        }
        return null;
    };

    const isLoading =
        saveCarbonSourceRes.isLoading || updateCarbonSourceRes.isLoading ||
        saveMonitoringBatchRes.isLoading || updateMonitoringBatchRes.isLoading ||
        saveVerificationRecordRes.isLoading || updateVerificationRecordRes.isLoading ||
        saveProjectRes.isLoading || updateProjectRes.isLoading ||
        bulkUploadCarbonSourceRes.isLoading || bulkUploadMonitoringBatchRes.isLoading ||
        bulkUploadVerificationRecordRes.isLoading;

    return (
        <Modal show={props.show} onHide={handleClose} size="lg" centered scrollable>
            <Modal.Header closeButton className="bg-cm-light-primary1 border-0">
                <Modal.Title className="text-accent fw-bold">
                    {props.actionType === ACTION_TYPE.ADD_ACTION ? "Add" : "Update"} {props.reportName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading && <Loading />}

                {/* Tab switch */}
                {props.actionType === ACTION_TYPE.ADD_ACTION && (
                    <div className="d-flex mb-3 border-bottom pb-2">
                        <button type="button" className={`btn btn-sm me-2 ${activeTab === "manual" ? "bg-accent text-white" : "btn-outline-secondary"}`} onClick={() => setActiveTab("manual")}>
                            Manual Entry
                        </button>
                        <button type="button" className={`btn btn-sm ${activeTab === "bulk" ? "bg-accent text-white" : "btn-outline-secondary"}`} onClick={() => setActiveTab("bulk")}>
                            Bulk Upload
                        </button>
                    </div>
                )}

                {activeTab === "manual" ? (
                    <Formik initialValues={getInitialValues()} enableReinitialize onSubmit={handleSubmit}>
                        {({ values, setFieldValue, handleSubmit }) => (
                            <Form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Report-specific fields first */}
                                    {renderSpecificFields(values, setFieldValue)}
                                    {/* Common relation dropdowns */}
                                    {renderCommonFields(values, setFieldValue)}
                                </div>
                                <div className="d-flex justify-content-end mt-2">
                                    <button type="button" className="btn btn-secondary me-2" onClick={handleClose}>Cancel</button>
                                    <button type="submit" className="btn border-0 text-white" style={{ background: "var(--accent, #2563eb)" }}>
                                        {props.actionType === ACTION_TYPE.ADD_ACTION ? "Save" : "Update"}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                ) : (
                    <div>
                        <p className="text-muted small">Upload an XLSX file to bulk import records.</p>
                        <div className="mb-3">
                            <label className="form-label fw-semibold">Select XLSX File</label>
                            <input type="file" accept=".xlsx" className="form-control" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        </div>
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn btn-secondary me-2" onClick={handleClose}>Cancel</button>
                            <button type="button" className="btn border-0 text-white" style={{ background: "var(--accent, #2563eb)" }} onClick={handleBulkUpload}>Upload</button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default CarbonMasterData;
